'use client';
import React from 'react';
import { ImageProps } from '@repo/ui/src/types/image';
import ProductOverlay from '@components/Product/Overlay';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';
import { YoutubeLinkProps } from '@repo/ui/src/types/youtubeLink';
import LottiePlayer from '@components/LottiePlayer';
import Accordion, { AccordionItem } from '@repo/ui/src/components/Accordion';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { BackgroundColor } from './_enums';
import { classNames } from '@repo/ui/src/utils/classNames';
import BackgroundVideo from '@repo/ui/src/components/BackgroundVideo';
import Image from 'next/image';

interface Feature {
  heading: string;
  description: string;
  youtubeLink?: YoutubeLinkProps;
  dropdownImage: ImageProps;
  overlay: ImageProps;
  animationUrl?: string;
}

interface FeatureExp extends Feature {
  isOpen: boolean;
}

interface Props {
  features: Feature[];
  backgroundTheme: BackgroundColor;
  sectionHeader: ToutContentProps;
}

const ProductAccordion = ({ features: _features, backgroundTheme, sectionHeader }: Props) => {
  const [width, setWidth] = React.useState(0);
  const videoContainer = React.useRef<HTMLDivElement>(null);
  const [features, setFeatures] = React.useState<FeatureExp[]>(
    _features?.map((feature, i) => ({ ...feature, isOpen: i === 0 })) ?? [],
  );
  const dark = backgroundTheme === 'Black' || backgroundTheme === 'Dark';

  React.useEffect(() => {
    if (videoContainer.current) {
      setWidth(videoContainer.current.offsetWidth);
    }
  }, [videoContainer]);

  const onClick = React.useCallback(
    ([index]: any) => {
      setFeatures((state) => {
        return state.map((feat, i) =>
          i === index ? { ...feat, isOpen: true } : { ...feat, isOpen: false },
        );
      });
    },
    [setFeatures],
  );

  const getBgColor = () => {
    if (backgroundTheme === 'Black' || backgroundTheme === 'Dark') {
      return 'bg-neutral-800';
    }
    return 'bg-warm-200';
  };

  return (
    <div
      className={classNames(
        'grid grid-rows-[min-content] relative py-20',
        'md:grid-cols-[1fr_32%] md:grid-rows-1 md:py-[110px]',
        'xl:grid-cols-[1fr_30%] xl:py-[145px]',
        getBgColor(),
      )}
    >
      <div className="relative">
        <div className="aspect-video sticky top-[140px] md:aspect-[5/4]">
          <ToutContent
            align="left"
            headingLight={sectionHeader?.headingLight}
            heading={sectionHeader?.heading}
            headerVariation={sectionHeader?.headerVariation}
            dark={dark}
            className="ml-6 md:z-[1] md:ml-10 lg:ml-20 tout-content"
          />
          <div className="relative w-full h-full md:mt-[-25%]">
            <ProductOverlay
              images={features.map((feat) => ({
                overlayImage: feat?.overlay,
                checked: feat?.isOpen,
              }))}
              imageWidth={1100}
              display
            />
            {features
              .filter((feat) => !!feat?.animationUrl)
              .map((feat) => (
                <LottiePlayer
                  key={feat?.heading}
                  animationUrl={feat.animationUrl!}
                  isPlaying={feat.isOpen}
                  isStopped={!feat.isOpen}
                />
              ))}
          </div>
        </div>
      </div>
      <div className="px-6 md:pr-10 md:pl-0 lg:pr-20 lg:pl-0">
        <Accordion
          preExpanded={[0]}
          onChange={onClick}
          divider
          dark={dark}
          className="border-neutral-300 last:border-b"
        >
          {features.map((item, i) => {
            const dropdownImage =
              item.dropdownImage?.image && buildImageSrc(item.dropdownImage, 500);
            return (
              <AccordionItem
                key={i}
                uuid={i}
                heading={item.heading}
                headerClassName="border-t border-neutral-300"
              >
                <p
                  className={classNames(
                    dark ? 'text-neutral-100' : 'text-neutral-700',
                    'tra-text-web-body-xl',
                  )}
                >
                  {item.description}
                </p>
                {(item.youtubeLink?.videoAsset || item.dropdownImage) && (
                  <div className="mt-4 w-full md:mt-6" ref={videoContainer}>
                    {item.youtubeLink?.videoAsset && (
                      <BackgroundVideo
                        video={item.youtubeLink.videoAsset}
                        width={width}
                        fillContainer
                        playButton
                      />
                    )}
                    {dropdownImage && (
                      <div className="relative aspect-video">
                        <Image
                          src={dropdownImage}
                          fill
                          objectFit="cover"
                          priority={true}
                          alt={item.dropdownImage.altTag || ''}
                        />
                      </div>
                    )}
                  </div>
                )}
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </div>
  );
};

export default ProductAccordion;
